import React, { Component } from "react";
import { Container, Row, Col, Form } from "react-bootstrap"
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import axios from "axios";
import Swal from "sweetalert2";
import Select from '../../../Components/PCSelect';
import PageHeader from '../shared/PageHeader';

/* Role Rights — restores legacy Admin/RoleRights.aspx
   (SP_RE_Admin_GetMenuActions_RoleRights + SP_RE_MenuActionRights_Save).

   A checkbox per (menu, action), grouped by section, for one role at a time. Saving replaces
   that role's grants wholesale in a single transaction, exactly as the legacy proc did.

   This is the screen that actually decides two things at once:
     - what appears in the user's sidebar (AdminLayout renders from these grants), and
     - what the API will allow, because middlewares/Authz.php resolves each endpoint to the
       screens that use it and checks the same table.
   So removing a tick here removes both the menu item and the API access behind it. */

class RoleRights extends Component {
    constructor(props) {
        super(props);
        this.state = {
            roles: [],
            role: "",
            menus: [],
            granted: {},      // MENU_ACTION_ID -> true
            loaded: false,
            dirty: false
        };
    }

    componentDidMount() {
        this.loadRoles()
    }

    async loadRoles() {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Setups/AppRole.php`, {
                totalRecordsPerPage: 200, recordOffset: 1
            });
            const roles = (res.data.success === 1 ? res.data.data : []).map(r => ({
                value: r.ROLE_CODE,
                label: `${r.ROLE_NAME} (role ${r.PC_USR_ROLE_ID} / group ${r.PC_GRP_ID})`
            }));
            this.setState({ roles })
        } catch (error) {
            console.error('loadRoles error :>> ', error);
        }
    }

    async loadRights(roleCode) {
        this.props.toggleLoader && this.props.toggleLoader(true)
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/RoleRights.php`, {
                ROLE_CODE: roleCode
            });
            if (res.data.success === 1) {
                const granted = {};
                res.data.data.menus.forEach(m => m.actions.forEach(a => {
                    if (a.GRANTED) granted[a.MENU_ACTION_ID] = true
                }));
                this.setState({ menus: res.data.data.menus, granted, loaded: true, dirty: false })
            } else {
                this.setState({ menus: [], granted: {}, loaded: false })
                Swal.fire('Error', res.data.message || 'Could not load rights.', 'error')
            }
        } catch (error) {
            console.error('loadRights error :>> ', error);
            Swal.fire('Error', 'Could not load rights.', 'error')
        }
        this.props.toggleLoader && this.props.toggleLoader(false)
    }

    onRoleChange(opt) {
        this.setState({ role: opt || "", menus: [], granted: {}, loaded: false, dirty: false }, () => {
            if (opt) this.loadRights(opt.value)
        })
    }

    toggle(id) {
        this.setState(prev => {
            const granted = { ...prev.granted };
            if (granted[id]) delete granted[id]; else granted[id] = true;
            return { granted, dirty: true }
        })
    }

    // Whole-row helper: a menu's actions are usually granted or revoked together.
    toggleMenu(menu, on) {
        this.setState(prev => {
            const granted = { ...prev.granted };
            menu.actions.forEach(a => {
                if (on) granted[a.MENU_ACTION_ID] = true; else delete granted[a.MENU_ACTION_ID];
            });
            return { granted, dirty: true }
        })
    }

    async save() {
        const { role, granted } = this.state;
        if (!role) return;

        const ids = Object.keys(granted).map(Number);

        // Saving zero rights leaves that role with no menu and no API access at all, which is a
        // big enough consequence to confirm rather than silently apply.
        if (ids.length === 0) {
            const ok = await Swal.fire({
                title: 'Remove every permission?',
                text: `${role.label} will lose all menu items and all API access.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Remove all',
                confirmButtonColor: '#a32f2f'
            });
            if (!ok.isConfirmed) return
        }

        this.props.toggleLoader && this.props.toggleLoader(true)
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/mutation/RoleRights.php`, {
                ROLE_CODE: role.value,
                MENU_ACTION_IDS: ids
            });
            if (res.data.success === 1) {
                this.setState({ dirty: false })
                Swal.fire('Saved', `${res.data.saved} permission(s) saved. Users see the change on their next sign-in or token refresh.`, 'success')
                this.loadRights(role.value)
            } else {
                Swal.fire('Error', res.data.message || 'Could not save rights.', 'error')
            }
        } catch (error) {
            console.error('save rights error :>> ', error);
            Swal.fire('Error', 'Could not save rights.', 'error')
        }
        this.props.toggleLoader && this.props.toggleLoader(false)
    }

    render() {
        const { roles, role, menus, granted, loaded, dirty } = this.state;

        // Group by section for readability, preserving the order the endpoint returned.
        const sections = [];
        menus.filter(m => !m.IS_SECTION).forEach(m => {
            const name = m.SECTION || 'Other';
            let s = sections.find(x => x.name === name);
            if (!s) { s = { name, items: [] }; sections.push(s) }
            s.items.push(m)
        });

        const headCell = { background: '#f1f1f1', color: '#111827', fontWeight: 600 };

        return (
            <Container>
                <PageHeader breadcrumb="section" title="Role Rights" />

                <Row style={{ alignItems: 'center', marginTop: '10px' }} >
                    <Col md={5}>
                        <Form.Group className="mb-3" >
                            <Form.Label>Role</Form.Label>
                            <Select menuPortalTarget={document.body}
                                menuPlacement="auto"
                                value={role}
                                options={roles}
                                onChange={this.onRoleChange.bind(this)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Button className="add-button" size="small" variant="contained"
                            disabled={!loaded || !dirty}
                            onClick={this.save.bind(this)} >
                            Save Rights
                        </Button>
                        {dirty && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#a32f2f' }} >
                            unsaved changes
                        </span>}
                    </Col>
                </Row>

                {loaded &&
                    <Row>
                        <Col md={12}>
                            <div style={{ fontSize: '12px', color: '#555', padding: '4px 0 10px' }} >
                                Unticking a row removes both the sidebar item and the API access behind it.
                            </div>
                            {sections.map((sec, si) => (
                                <Paper key={si} style={{ marginBottom: '14px' }} >
                                    <div style={{
                                        padding: '6px 12px', fontWeight: 700, background: '#ffefd6',
                                        fontSize: '13px'
                                    }} >{sec.name}</div>
                                    <TableContainer>
                                        <Table size="small" aria-label={`${sec.name} rights`}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={headCell} >Screen</TableCell>
                                                    <TableCell style={headCell} >All</TableCell>
                                                    {(sec.items[0]?.actions || []).map((a, ai) => (
                                                        <TableCell key={ai} style={{ ...headCell, textAlign: 'center' }} >
                                                            {a.ACTION_NAME}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sec.items.map((m, mi) => {
                                                    const all = m.actions.every(a => granted[a.MENU_ACTION_ID]);
                                                    return (
                                                        <TableRow key={mi} hover >
                                                            <TableCell>
                                                                {m.MENU_TEXT}
                                                                <span style={{ color: '#999', fontSize: '11px', marginLeft: '6px' }} >
                                                                    {m.MENU_URL}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Checkbox size="small" style={{ padding: 0 }}
                                                                    checked={all}
                                                                    onChange={() => this.toggleMenu(m, !all)} />
                                                            </TableCell>
                                                            {m.actions.map((a, ai) => (
                                                                <TableCell key={ai} align="center">
                                                                    <Checkbox size="small" style={{ padding: 0 }}
                                                                        checked={!!granted[a.MENU_ACTION_ID]}
                                                                        onChange={() => this.toggle(a.MENU_ACTION_ID)} />
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            ))}
                        </Col>
                    </Row>
                }
            </Container>
        );
    }
}

export default RoleRights;
